import { NestedTreeControl } from '@angular/cdk/tree';
import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { Router, RouterModule } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { BehaviorSubject } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { CasoUsoService } from '@services/casoUso/casoUso.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { MatTableDataSource } from '@angular/material/table';
import { CasoUsoDto } from '@models/seguridad/casosUso/casoUsoDto.model';
import { AppRolesDirective } from '@directives/appRoles/app-roles.directive';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';

interface TreeNode {
  id: number;
  name: string;
  icon?: string;
  url?: string;
  children?: TreeNode[];
  casoUso?: CasoUsoDto; // Agregar referencia al caso de uso completo
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTreeModule,
    NgxSpinnerModule,
    AppRolesDirective,
    MatTooltipModule,
    MatDialogModule,
    MatChipsModule,
    MatMenuModule
  ],
  selector: 'app-menues-desplegables',
  templateUrl: './menues-desplegables.component.html',
  styleUrls: ['./menues-desplegables.component.css'],
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0px', opacity: 0, overflow: 'hidden' })),
      state('expanded', style({ height: '*', opacity: 1 })),
      transition('collapsed <=> expanded', animate('300ms ease-in-out')),
    ])
  ]
})
export class MenuesDesplegablesComponent {
  filterText = '';
  treeControl = new NestedTreeControl<TreeNode>(node => node.children);
  dataSource = new MatTreeNestedDataSource<TreeNode>();
  originalTreeData!: TreeNode[];
  selectedNode: TreeNode | null = null;
  showDetails = false;
  allCasosUso: CasoUsoDto[] = []; // Guardar todos los casos de uso

  constructor(
    private casoUsoService: CasoUsoService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.getAllCasoUso();
  }

  convertirCasosDeUsoAgrupados(casos: CasoUsoDto[]): TreeNode[] {
    const menuMap: { [menuDescripcion: string]: TreeNode } = {};
    const categoriaGenerica = 'Sin Categoría';

    for (const caso of casos) {
      const menuDesc = caso.menu?.descripcion || categoriaGenerica;

      if (!menuMap[menuDesc]) {
        menuMap[menuDesc] = {
          name: menuDesc,
          id: caso.menu?.id || 0,
          icon: caso.menu?.icon || 'folder',
          children: []
        };
      }

      menuMap[menuDesc].children!.push({
        name: caso.descripcion,
        icon: caso.icon || 'description',
        url: caso.path,
        id: caso.id,
        casoUso: caso // Guardar referencia completa
      });
    }

    return Object.values(menuMap);
  }

  getAllCasoUso() {
    this.spinner.show('spListado');
    this.casoUsoService.getAll()
      .subscribe(
        data => {
          this.spinner.hide('spListado');
          this.allCasosUso = data;

          const filtrados = data.filter(caso => caso.esMenuDesplegable);
          const treeData = this.convertirCasosDeUsoAgrupados(filtrados);
          this.originalTreeData = treeData;
          this.dataSource.data = treeData;
        },
        error => {
          this.spinner.hide('spListado');
          this.snackBar.showError(error, "Error");
        }
      );
  }

  hasChild = (_: number, node: TreeNode) => !!node.children && node.children.length > 0;

  filterTree() {
    const filter = this.filterText.trim().toLowerCase();

    if (!filter) {
      this.dataSource.data = this.originalTreeData;
      this.treeControl.collapseAll();
      return;
    }

    const filtered = this.originalTreeData
      .map(parent => {
        const filteredChildren = parent.children?.filter(child =>
          child.name.toLowerCase().includes(filter)
        ) || [];

        if (filteredChildren.length > 0) {
          return {
            ...parent,
            children: filteredChildren
          };
        }

        return null;
      })
      .filter(Boolean) as TreeNode[];

    this.dataSource.data = filtered;
    this.treeControl.dataNodes = filtered;
    this.treeControl.expandAll();
  }

  // Nueva función para mostrar detalles
  showNodeDetails(node: TreeNode) {
    if (node.casoUso) {
      this.selectedNode = node;
      this.showDetails = true;
    }
  }

  // Función para cerrar detalles
  closeDetails() {
    this.showDetails = false;
    this.selectedNode = null;
  }

  // Función para obtener el estado como texto
  getEstadoTexto(casoUso: CasoUsoDto): string {
    const estados = [];
    if (casoUso.visible) estados.push('Visible');
    if (casoUso.habilitado) estados.push('Habilitado');
    if (casoUso.esMenuDesplegable) estados.push('Menú Desplegable');
    return estados.length > 0 ? estados.join(', ') : 'Sin estados activos';
  }

  private normalizeUrl(name: string): string {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
  }

  getFullUrl(node: TreeNode): string {
    return node?.id ? `/menusdesplegables/${node.id}` : '/menusdesplegables';
  }

 onNodeClick(node: TreeNode) {
    const url = this.routeFullUrl(node);
    this.router.navigate([url]);
  }

  routeFullUrl(node: TreeNode): string {
    return node?.id ? `/menusdesplegables/${node.id}` : '/menusdesplegables';
  }
}
